/**
 * Advanced Threads Submenu Component
 * 
 * A submenu for the Threads + menu that provides advanced features:
 * - Default Model: Quick access to change the default AI provider/model
 * - External Mode: Toggle between orchestrated and direct external model modes
 * 
 * This submenu is only visible when the user has Advanced Mode enabled.
 */

import React from 'react';
import { Settings, Sparkles, Zap, Check } from 'lucide-react';
import {
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';

export type ThreadMode = 'orchestrated' | 'external';

interface AdvancedThreadsSubmenuProps {
  /** Whether External Mode is currently enabled for this thread */
  externalModeEnabled: boolean;
  /** The currently selected external provider (when in external mode) */
  externalProvider?: string;
  /** The currently selected external model (when in external mode) */
  externalModel?: string;
  /** Called when user wants to open the Default Model sheet */
  onOpenDefaultModel: () => void;
  /** Called when user toggles External Mode */
  onToggleExternalMode: () => void;
  /** Close the parent dropdown menu */
  onCloseMenu: () => void;
}

export function AdvancedThreadsSubmenu({
  externalModeEnabled,
  externalProvider,
  externalModel,
  onOpenDefaultModel,
  onToggleExternalMode,
  onCloseMenu,
}: AdvancedThreadsSubmenuProps) {
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger className="flex items-center gap-2">
        <Settings className="w-4 h-4 text-purple-600" />
        <span>Advanced</span>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="w-56">
        <DropdownMenuLabel className="text-xs text-gray-500 uppercase">
          AI Settings
        </DropdownMenuLabel>

        {/* Default Model */}
        <DropdownMenuItem
          onClick={() => {
            onCloseMenu();
            onOpenDefaultModel();
          }}
          className="flex items-center gap-2"
        >
          <Sparkles className="w-3.5 h-3.5 text-purple-600" />
          <span>Default Model</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuLabel className="text-xs text-gray-500 uppercase">
          Thread Mode
        </DropdownMenuLabel>

        {/* Orchestrated Mode (default) */}
        <DropdownMenuItem
          onClick={() => {
            if (externalModeEnabled) {
              onToggleExternalMode();
            }
            onCloseMenu();
          }}
          className="flex items-center justify-between"
        >
          <div className="flex items-center gap-2">
            <Zap className="w-3.5 h-3.5 text-orange-600" />
            <div>
              <span>Orchestrated</span>
              <p className="text-sm text-gray-500">AI with tools & agents</p>
            </div>
          </div>
          {!externalModeEnabled && <Check className="w-4 h-4 text-orange-600" />}
        </DropdownMenuItem>

        {/* External Mode */}
        <DropdownMenuItem
          onClick={() => {
            if (!externalModeEnabled) {
              onToggleExternalMode();
            }
            onCloseMenu();
          }}
          className="flex items-center justify-between"
        >
          <div className="flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-purple-600" />
            <div>
              <span>External</span>
              <p className="text-sm text-gray-500">Direct model access</p>
            </div>
          </div>
          {externalModeEnabled && <Check className="w-4 h-4 text-purple-600" />}
        </DropdownMenuItem>

        {externalModeEnabled && externalProvider && (
          <>
            <DropdownMenuSeparator />
            <div className="px-2 py-1.5">
              <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                {externalProvider}/{externalModel || 'default'}
              </Badge>
            </div>
          </>
        )}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}

export default AdvancedThreadsSubmenu;

